import { cached, tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { fn } from '@ember/helper';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import RSelect from 'tio-reimagined/components/select';
import Input from 'tio-reimagined/components/input';
import Textarea from 'tio-reimagined/components/textarea';
import * as v from 'valibot';
import { on } from '@ember/modifier';
import { not, or } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import { trackedObject } from '@ember/reactive/collections';
import {
  buildCustomFieldsFormModelForValidation,
  buildValidationSchemaForCustomFields,
  buildValidationSchemaForProgramTemplateFields,
  copyFieldsAndUpdatePerProgramTemplate,
  getSingleValueForTasField,
  isTasFieldComponentType,
  setSingleValueForTasField,
  validateAndCollectErrors,
} from '../../../utils/tuition-assistance/fields.ts';
import Component from '@glimmer/component';
import CustomFieldsForProvince from '../forms/custom-fields-for-province.gts';
import { divide } from 'tio-ui/math';
import errorsForField from '../../../helpers/errors-for-field.ts';
import inputmask from '../../../modifiers/inputmask.ts';
import ProgramFormField from '../program-form-field.gts';
import SchoolSelect from './school-select.gts';
import ErrorMessages from 'tio-reimagined/components/error-messages';
import type { CustomFieldSignature } from '../../../types/tuition-assistance';
import type { FieldName } from '../../../utils/tuition-assistance/fields.ts';
import type RouterService from '@ember/routing/router';
import type Store from '@ember-data/store';
import type TasProgramInstanceModel from '../../../models/tas-program-instance.ts';
import type TasUnifiedInstitutionModel from '../../../models/tas-unified-institution.ts';
import type Owner from '@ember/owner';
import type TasProgramTemplateInstitutionModel from '../../../models/tas-program-template-institution.ts';
import type { TasUnifiedInstitution } from '../../../models/search.ts';

export interface TuitionAssistanceEditProgramInformationModalSignature {
  Args: {
    programInstance: TasProgramInstanceModel;
    selectedSchool?: TasUnifiedInstitutionModel;
    isOpen: boolean;
    onClose: () => void;
  };
}

const changelogSchema = v.pipe(v.string('Comment is required'), v.nonEmpty('Comment is required'));
const number = Number;

export default class TuitionAssistanceEditProgramInformationModal extends Component<TuitionAssistanceEditProgramInformationModalSignature> {
  @service declare router: RouterService;
  @service declare store: Store;

  customFieldsProvince = 'PROGRAM';
  fieldsCopy!: TasProgramInstanceModel['fields'];
  @tracked customFieldsCopy: TasProgramInstanceModel['customFields'];
  @tracked selectedSchool?: TasUnifiedInstitutionModel | TasUnifiedInstitution;

  @tracked hasSubmitted = false;
  @tracked submitError = null;
  @tracked changelog = '';

  constructor(owner: Owner, args: TuitionAssistanceEditProgramInformationModalSignature['Args']) {
    super(owner, args);
    this.fieldsCopy = trackedObject(this.args.programInstance.fields);
    // inits a fresh copy to introduce possible new fields added to the program template
    const programTemplateCustomFields =
      this.args.programInstance.tasProgramTemplate?.instanceCustomFields || [];
    this.customFieldsCopy = copyFieldsAndUpdatePerProgramTemplate(
      this.args.programInstance.customFields,
      programTemplateCustomFields
    );
    this.setSelectedSchoolIfExists();
  }

  setSelectedSchoolIfExists() {
    if (this.args.selectedSchool) {
      this.selectedSchool = this.args.selectedSchool;
    }
  }

  featuredSchoolData = trackedFunction(this, async () => {
    const template = this.args.programInstance.tasProgramTemplate;
    (await this.store.query<TasProgramTemplateInstitutionModel>(
      'tas-program-template-institution',
      {
        filter: {
          'is-featured': true,
          'tas-program-template': template.id,
        },
        include: 'accredited-institution,unaccredited-institution,logo',
      }
    )) as TasProgramTemplateInstitutionModel[];
  });

  get featuredSchools() {
    return this.featuredSchoolData.value || [];
  }

  get renderInstitutionAsSchoolSelect() {
    const programTemplate = this.args.programInstance.tasProgramTemplate;
    if (!programTemplate?.fields) {
      return true;
    }

    return isTasFieldComponentType(
      'SCHOOL_INSTITUTION_NAME',
      programTemplate.fields,
      'school-select'
    );
  }

  get formModelForValidation() {
    const customFieldsFormModelForProvince = buildCustomFieldsFormModelForValidation(
      this.customFieldsForProvince
    );
    const programBegin: string = getSingleValueForTasField(
      'ESTIMATED_PROGRAM_BEGIN',
      this.fieldsCopy
    );
    const programEnd: string = getSingleValueForTasField(
      'ESTIMATED_PROGRAM_COMPLETION',
      this.fieldsCopy
    );

    return {
      SCHOOL_INSTITUTION_NAME: getSingleValueForTasField(
        'SCHOOL_INSTITUTION_NAME',
        this.fieldsCopy
      ),
      PROGRAM_SPECIALIZATION: getSingleValueForTasField('PROGRAM_SPECIALIZATION', this.fieldsCopy),
      PROGRAM_MAJOR: getSingleValueForTasField('PROGRAM_MAJOR', this.fieldsCopy),
      ESTIMATED_PROGRAM_BEGIN: this.formatDateForValidation(programBegin),
      ESTIMATED_PROGRAM_COMPLETION: this.formatDateForValidation(programEnd),
      ESTIMATED_COST: getSingleValueForTasField('ESTIMATED_COST', this.fieldsCopy),
      ENROLLMENT_OBJECTIVE: getSingleValueForTasField('ENROLLMENT_OBJECTIVE', this.fieldsCopy),
      ...customFieldsFormModelForProvince,
    };
  }

  formatDateForValidation(date: string) {
    return date ? new Date(date) : undefined;
  }

  get changelogValidationErrors() {
    const result = v.safeParse(changelogSchema, this.changelog);
    if (!result.success) {
      return result.issues.map((issue) => issue.message);
    }
    return [];
  }

  @cached
  get dynamicValidationSchema() {
    const fields = [
      {
        name: 'SCHOOL_INSTITUTION_NAME',
        rules: { type: 'string' },
      },
      {
        name: 'PROGRAM_SPECIALIZATION',
        rules: { type: 'string' },
      },
      {
        name: 'PROGRAM_MAJOR',
        rules: { type: 'string' },
      },
      {
        name: 'ESTIMATED_PROGRAM_BEGIN',
        rules: { type: 'date', min: '2000-01-01', max: '2099-01-01' },
        errors: {
          min: 'Date must be after Jan 1, 2000',
          max: 'Date must be before Jan 1, 2099',
        },
      },
      {
        name: 'ESTIMATED_PROGRAM_COMPLETION',
        rules: { type: 'date', min: 'ESTIMATED_PROGRAM_BEGIN', max: '2099-01-01' },
        errors: {
          min: 'Date must be after program begin date',
          max: 'Date must be before Jan 1, 2099',
        },
      },
      {
        name: 'ESTIMATED_COST',
        rules: { type: 'number', typeError: 'Value must be a number' },
      },
      {
        name: 'ENROLLMENT_OBJECTIVE',
        rules: { type: 'string' },
      },
    ];
    return buildValidationSchemaForProgramTemplateFields(
      // @ts-expect-error: not sure about this one
      fields,
      this.args.programInstance?.tasProgramTemplate
    );
  }

  @cached
  get dynamicValidationSchemaForCustomFields() {
    return buildValidationSchemaForCustomFields(this.customFieldsForProvince);
  }

  get customFieldsForProvince() {
    return this.customFieldsCopy.filter((field) => field.province === this.customFieldsProvince);
  }

  get isFormValid() {
    return !this.formValidationErrors.length && !this.changelogValidationErrors.length;
  }

  get formValidationErrors() {
    return validateAndCollectErrors(
      this.formModelForValidation,
      this.dynamicValidationSchema,
      this.dynamicValidationSchemaForCustomFields
    );
  }

  updateValueForField = (fieldName: FieldName, raw?: string | null, event?: Event) => {
    let valueToSet: string | number = raw ?? '';

    const target = event?.target as HTMLInputElement | undefined;
    // @ts-expect-error: input mask
    if (target?.inputmask?.userOptions?.alias === 'currency') {
      valueToSet = Number(target.inputmask.unmaskedvalue()) * 100;
    }
    // @ts-expect-error: overload matching
    setSingleValueForTasField(fieldName, valueToSet, this.fieldsCopy);
  };

  didUpdateCustomFields = (updated: CustomFieldSignature[] = []) => {
    this.customFieldsCopy = updated;
  };

  didSelectSchool = (searchResult?: TasUnifiedInstitution) => {
    this.selectedSchool = searchResult;
    this.updateValueForField('SCHOOL_INSTITUTION_NAME', searchResult?.locationName);

    // TasUnifiedInstitution from search has 'institutionId', TasUnifiedInstitutionModel has 'institutionId'
    const institutionId = searchResult?.institutionId || undefined;
    this.updateValueForField('TAS_INSTITUTION_ID', institutionId);

    // TasUnifiedInstitution from search has 'dapipId', TasUnifiedInstitutionModel has 'dapipId'
    const dapipId = searchResult?.dapipId?.toString();
    this.updateValueForField('DAPIP_INSTITUTION_ID', dapipId);
  };

  submit = () => {
    this.saveFields.perform();
    this.args.onClose();
    this.changelog = '';
    this.hasSubmitted = false;
  };

  saveFields = dropTask(async () => {
    this.hasSubmitted = true;
    this.submitError = null;
    if (!this.isFormValid) {
      return;
    }

    this.args.programInstance.fields = this.fieldsCopy;
    this.args.programInstance.customFields = this.customFieldsCopy;

    try {
      await this.args.programInstance.save();
    } catch (error) {
      this.submitError = error;
    }
  });

  <template>
    <Modal @isOpen={{@isOpen}} @onClose={{@onClose}} @size="xl">
      <:header>
        Program Information
      </:header>
      <:body>
        <div>
          <div class="">
            <form class="mx-8 grid grid-cols-1 gap-4">
              {{#if this.renderInstitutionAsSchoolSelect}}
                <SchoolSelect
                  @programTemplate={{@programInstance.tasProgramTemplate}}
                  @placeholder={{or
                    (String
                      (getSingleValueForTasField "SCHOOL_INSTITUTION_NAME" @programInstance.fields)
                    )
                    "Enter institution name"
                  }}
                  @selected={{this.selectedSchool}}
                  @onChange={{this.didSelectSchool}}
                  @errors={{errorsForField
                    "SCHOOL_INSTITUTION_NAME"
                    schemaErrors=this.formValidationErrors
                  }}
                  class="my-4"
                />
              {{else}}
                <ProgramFormField
                  @name="SCHOOL_INSTITUTION_NAME"
                  @programTemplate={{@programInstance.tasProgramTemplate}}
                  class="my-4"
                  as |field|
                >
                  {{#if field.isFieldOptionPicklist}}
                    <RSelect
                      data-test-school-fallback-select={{field.name}}
                      @items={{field.fieldOptions}}
                      @selected={{getSingleValueForTasField field.name this.fieldsCopy}}
                      @onChange={{fn this.updateValueForField field.name}}
                      @errors={{if
                        field.isTouched
                        (errorsForField field.name schemaErrors=this.formValidationErrors)
                      }}
                    />
                  {{else}}
                    <Input
                      data-test-school-fallback-input={{field.name}}
                      id={{field.inputId}}
                      @value={{getSingleValueForTasField field.name this.fieldsCopy}}
                      @onChange={{fn this.updateValueForField field.name}}
                      @errors={{if
                        field.isTouched
                        (errorsForField field.name schemaErrors=this.formValidationErrors)
                      }}
                      {{on "blur" field.markTouched}}
                    />
                  {{/if}}
                </ProgramFormField>
              {{/if}}
              <ProgramFormField
                @name="PROGRAM_SPECIALIZATION"
                @programTemplate={{@programInstance.tasProgramTemplate}}
                class="my-4"
                as |field|
              >
                {{#if field.isFieldOptionPicklist}}
                  <RSelect
                    data-test={{field.name}}
                    @items={{field.fieldOptions}}
                    @selected={{getSingleValueForTasField field.name this.fieldsCopy}}
                    @onChange={{fn this.updateValueForField field.name}}
                    @errors={{if
                      field.isTouched
                      (errorsForField field.name schemaErrors=this.formValidationErrors)
                    }}
                  />
                {{else}}
                  <Input
                    data-test={{field.name}}
                    id={{field.inputId}}
                    @value={{getSingleValueForTasField field.name this.fieldsCopy}}
                    @onChange={{fn this.updateValueForField field.name}}
                    @errors={{if
                      field.isTouched
                      (errorsForField field.name schemaErrors=this.formValidationErrors)
                    }}
                    {{on "blur" field.markTouched}}
                  />
                {{/if}}
              </ProgramFormField>
              <ProgramFormField
                @name="PROGRAM_MAJOR"
                @programTemplate={{@programInstance.tasProgramTemplate}}
                class="my-4"
                as |field|
              >
                {{#if field.isFieldOptionPicklist}}
                  <RSelect
                    data-test={{field.name}}
                    @items={{field.fieldOptions}}
                    @selected={{getSingleValueForTasField field.name this.fieldsCopy}}
                    @onChange={{fn this.updateValueForField field.name}}
                    @errors={{if
                      field.isTouched
                      (errorsForField field.name schemaErrors=this.formValidationErrors)
                    }}
                  />
                {{else}}
                  <Input
                    data-test={{field.name}}
                    id={{field.inputId}}
                    @value={{getSingleValueForTasField field.name this.fieldsCopy}}
                    @onChange={{fn this.updateValueForField field.name}}
                    @errors={{if
                      field.isTouched
                      (errorsForField field.name schemaErrors=this.formValidationErrors)
                    }}
                    {{on "blur" field.markTouched}}
                  />
                {{/if}}
              </ProgramFormField>
              <ProgramFormField
                @name="ESTIMATED_PROGRAM_BEGIN"
                @programTemplate={{@programInstance.tasProgramTemplate}}
                class="my-4"
                as |field|
              >
                <Input
                  data-test={{field.name}}
                  id={{field.inputId}}
                  type="date"
                  @value={{getSingleValueForTasField field.name this.fieldsCopy}}
                  @onChange={{fn this.updateValueForField field.name}}
                  @errors={{if
                    field.isTouched
                    (errorsForField field.name schemaErrors=this.formValidationErrors)
                  }}
                  {{on "blur" field.markTouched}}
                />
              </ProgramFormField>
              <ProgramFormField
                @name="ESTIMATED_PROGRAM_COMPLETION"
                @programTemplate={{@programInstance.tasProgramTemplate}}
                class="my-4"
                as |field|
              >
                <Input
                  data-test={{field.name}}
                  id={{field.inputId}}
                  type="date"
                  @value={{getSingleValueForTasField field.name this.fieldsCopy}}
                  @onChange={{fn this.updateValueForField field.name}}
                  @errors={{if
                    field.isTouched
                    (errorsForField field.name schemaErrors=this.formValidationErrors)
                  }}
                  {{on "blur" field.markTouched}}
                />
                <p class="text-xs text-tio-gray-600 mt-3">
                  {{t "tuition_assistance.preapproval_app.program_hint"}}
                </p>
              </ProgramFormField>
              <ProgramFormField
                @name="ESTIMATED_COST"
                @programTemplate={{@programInstance.tasProgramTemplate}}
                class="my-4"
                as |field|
              >
                {{#if field.isFieldOptionPicklist}}
                  <RSelect
                    data-test={{field.name}}
                    @items={{field.fieldOptions}}
                    @selected={{getSingleValueForTasField field.name this.fieldsCopy}}
                    @onChange={{fn this.updateValueForField field.name}}
                    @errors={{if
                      field.isTouched
                      (errorsForField field.name schemaErrors=this.formValidationErrors)
                    }}
                  />
                {{else}}
                  <Input
                    data-test={{field.name}}
                    id={{field.inputId}}
                    {{! initial value is the original field value, not the copy, because of inputmask on FormInput being funny }}
                    @value={{String
                      (divide
                        (or (number (getSingleValueForTasField field.name this.fieldsCopy)) 0) 100
                      )
                    }}
                    @onChange={{fn this.updateValueForField field.name}}
                    @errors={{errorsForField field.name schemaErrors=this.formValidationErrors}}
                    {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
                    {{on "blur" field.markTouched}}
                  />
                {{/if}}
              </ProgramFormField>

              <ProgramFormField
                @name="ENROLLMENT_OBJECTIVE"
                @programTemplate={{@programInstance.tasProgramTemplate}}
                class="my-4"
                as |field|
              >
                {{#if field.isFieldOptionPicklist}}
                  <RSelect
                    data-test={{field.name}}
                    @items={{field.fieldOptions}}
                    @selected={{getSingleValueForTasField field.name this.fieldsCopy}}
                    @onChange={{fn this.updateValueForField field.name}}
                    @errors={{if
                      field.isTouched
                      (errorsForField field.name schemaErrors=this.formValidationErrors)
                    }}
                  />
                {{else}}
                  <Textarea
                    data-test={{field.name}}
                    id={{field.inputId}}
                    @value={{getSingleValueForTasField field.name this.fieldsCopy}}
                    @onChange={{fn this.updateValueForField field.name}}
                    @errors={{if
                      field.isTouched
                      (errorsForField field.name schemaErrors=this.formValidationErrors)
                    }}
                    {{on "blur" field.markTouched}}
                  />
                {{/if}}
              </ProgramFormField>

              <CustomFieldsForProvince
                @province={{this.customFieldsProvince}}
                @customFields={{this.customFieldsCopy}}
                @didUpdateFields={{this.didUpdateCustomFields}}
                @formValidationErrors={{this.formValidationErrors}}
                @fieldContainerClass="my-4"
                @hasSubmitted={{false}}
              />
              <div class="my-4">
                <label class="flex font-semibold text-sm text-tio-gray-700 block mb-1">
                  {{t "comment"}}
                  <span class="text-error-400 ml-1">*</span>
                </label>
                <Textarea
                  data-test-changelog
                  @value={{this.changelog}}
                  @onChange={{fn (mut this.changelog)}}
                  @errors={{if this.hasSubmitted this.changelogValidationErrors}}
                  @size="lg"
                  class="w-full"
                />
              </div>
            </form>
            <ErrorMessages
              @showErrors={{this.hasSubmitted}}
              @error={{this.submitError}}
              class="mb-2"
            />
          </div>
        </div>
      </:body>
      <:footer>
        <Footer
          @onSubmit={{this.submit}}
          @isRunning={{this.saveFields.isRunning}}
          @onCancel={{@onClose}}
          @submitDisabled={{not this.isFormValid}}
        />
      </:footer>
    </Modal>
  </template>
}
